import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface ContactInfo {
  phone: string;
  email: string;
  address: {
    street: string;
    city: string;
    region: string;
    postcode: string;
    country: string;
  };
  hours: string;
  serviceArea: string;
  visibility?: {
    phone?: boolean;
    email?: boolean;
    address?: boolean;
    hours?: boolean;
    serviceArea?: boolean;
  };
}

const ContactSettingsManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<ContactInfo | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["site-settings", "contact_info"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .eq("setting_key", "contact_info")
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Update formData when settings load
  useEffect(() => {
    if (settings?.setting_value) {
      const contactInfo = settings.setting_value as unknown as ContactInfo;
      setFormData({
        ...contactInfo,
        visibility: {
          phone: contactInfo.visibility?.phone ?? true,
          email: contactInfo.visibility?.email ?? true,
          address: contactInfo.visibility?.address ?? true,
          hours: contactInfo.visibility?.hours ?? true,
          serviceArea: contactInfo.visibility?.serviceArea ?? true,
        }
      });
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (contactInfo: ContactInfo) => {
      const { error } = await supabase
        .from("site_settings")
        .update({ setting_value: contactInfo as any })
        .eq("setting_key", "contact_info");

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings", "contact_info"] });
      toast({
        title: "Settings updated",
        description: "Contact information has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    // Basic validation
    if (!formData.email.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    updateMutation.mutate(formData);
  };

  if (isLoading || !formData) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contact Information Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="phone-toggle" className="text-sm text-muted-foreground">
                    Show on Contact Page
                  </Label>
                  <Switch
                    id="phone-toggle"
                    checked={formData.visibility?.phone ?? true}
                    onCheckedChange={(checked) =>
                      setFormData({
                        ...formData,
                        visibility: { ...formData.visibility, phone: checked }
                      })
                    }
                  />
                </div>
              </div>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="+44 1234 567890"
                required={formData.visibility?.phone !== false}
                disabled={formData.visibility?.phone === false}
                className={formData.visibility?.phone === false ? "opacity-50" : ""}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="email">Email Address</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="email-toggle" className="text-sm text-muted-foreground">
                    Show on Contact Page
                  </Label>
                  <Switch
                    id="email-toggle"
                    checked={formData.visibility?.email ?? true}
                    onCheckedChange={(checked) =>
                      setFormData({
                        ...formData,
                        visibility: { ...formData.visibility, email: checked }
                      })
                    }
                  />
                </div>
              </div>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="info@4fox.uk"
                required={formData.visibility?.email !== false}
                disabled={formData.visibility?.email === false}
                className={formData.visibility?.email === false ? "opacity-50" : ""}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Address</h3>
              <div className="flex items-center gap-2">
                <Label htmlFor="address-toggle" className="text-sm text-muted-foreground">
                  Show on Contact Page
                </Label>
                <Switch
                  id="address-toggle"
                  checked={formData.visibility?.address ?? true}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      visibility: { ...formData.visibility, address: checked }
                    })
                  }
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="street">Street Address</Label>
                <Input
                  id="street"
                  value={formData.address.street}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, street: e.target.value },
                    })
                  }
                  required={formData.visibility?.address !== false}
                  disabled={formData.visibility?.address === false}
                  className={formData.visibility?.address === false ? "opacity-50" : ""}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.address.city}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value },
                    })
                  }
                  required={formData.visibility?.address !== false}
                  disabled={formData.visibility?.address === false}
                  className={formData.visibility?.address === false ? "opacity-50" : ""}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="region">Region/County</Label>
                <Input
                  id="region"
                  value={formData.address.region}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, region: e.target.value },
                    })
                  }
                  required={formData.visibility?.address !== false}
                  disabled={formData.visibility?.address === false}
                  className={formData.visibility?.address === false ? "opacity-50" : ""}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode</Label>
                <Input
                  id="postcode"
                  value={formData.address.postcode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, postcode: e.target.value },
                    })
                  }
                  required={formData.visibility?.address !== false}
                  disabled={formData.visibility?.address === false}
                  className={formData.visibility?.address === false ? "opacity-50" : ""}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.address.country}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, country: e.target.value },
                    })
                  }
                  required={formData.visibility?.address !== false}
                  disabled={formData.visibility?.address === false}
                  className={formData.visibility?.address === false ? "opacity-50" : ""}
                />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="hours">Business Hours</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="hours-toggle" className="text-sm text-muted-foreground">
                    Show on Contact Page
                  </Label>
                  <Switch
                    id="hours-toggle"
                    checked={formData.visibility?.hours ?? true}
                    onCheckedChange={(checked) =>
                      setFormData({
                        ...formData,
                        visibility: { ...formData.visibility, hours: checked }
                      })
                    }
                  />
                </div>
              </div>
              <Input
                id="hours"
                value={formData.hours}
                onChange={(e) =>
                  setFormData({ ...formData, hours: e.target.value })
                }
                placeholder="24/7 - Available Around the Clock"
                required={formData.visibility?.hours !== false}
                disabled={formData.visibility?.hours === false}
                className={formData.visibility?.hours === false ? "opacity-50" : ""}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="serviceArea">Service Area</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="serviceArea-toggle" className="text-sm text-muted-foreground">
                    Show on Contact Page
                  </Label>
                  <Switch
                    id="serviceArea-toggle"
                    checked={formData.visibility?.serviceArea ?? true}
                    onCheckedChange={(checked) =>
                      setFormData({
                        ...formData,
                        visibility: { ...formData.visibility, serviceArea: checked }
                      })
                    }
                  />
                </div>
              </div>
              <Input
                id="serviceArea"
                value={formData.serviceArea}
                onChange={(e) =>
                  setFormData({ ...formData, serviceArea: e.target.value })
                }
                placeholder="Nationwide UK Coverage"
                required={formData.visibility?.serviceArea !== false}
                disabled={formData.visibility?.serviceArea === false}
                className={formData.visibility?.serviceArea === false ? "opacity-50" : ""}
              />
            </div>
          </div>

          <div className="flex justify-between items-center pt-4">
            <p className="text-sm text-muted-foreground">
              {settings?.updated_at && `Last updated: ${new Date(settings.updated_at).toLocaleDateString()}`}
            </p>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactSettingsManagement;
